import { Avatar, Box, CircularProgress, InputBase, Stack, styled, Tooltip } from '@mui/material'
import {
    ArrowBendDownRightIcon,
    CaretDownIcon,
    CaretUpIcon,
    ChatCenteredIcon,
    CheckIcon,
    EyeIcon,
    PencilSimpleIcon,
    TrashIcon,
    UserCircleDashedIcon,
    UserCircleIcon,
    XIcon,
} from '@phosphor-icons/react'
import {
    ContentChip,
    DisplayUserLink,
    getLocalizedDateString,
    isoDateStringToLocalizedTime,
    Modal,
    TinyButton,
    tokens,
    User,
} from 'ComponentLibrary'
import { IconButton, IconButtonWithOptions, Typography } from 'ComponentLibrary/src/piazza'
import React, { useEffect, useRef, useState } from 'react'
import { AnnotationMessage, DeviationAnnotation, GeneralAnnotation, PdfAnnotation } from './types'
import type { UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

export type TextBubbleCopy = {
    actions: {
        edit: string
        deleteAnnotation: string
        deleteThread: string
        delete: string
        cancel: string
        reply: string
        close: string
    }
    unknownUser: string
    createdInDraft: (draftVersion?: string) => string
    editAnnotationPlaceholder: string
    editReplyPlaceholder: string
    createAnnotationPlaceholder: string
    createReplyPlaceholder: string
    deletedAnnotation: string
    edited: string
    deleteModalTitle: string
    deleteConfirmation: (includeReplies?: boolean) => string
    notificationTooltip: string
    immediatelyVisible: string
}

const DEFAULT_TEXT_BUBBLE_COPY: TextBubbleCopy = {
    actions: {
        edit: 'Bearbeiten',
        deleteAnnotation: 'Anmerkung löschen',
        deleteThread: 'Ganze Kette löschen',
        delete: 'Löschen',
        cancel: 'Abbrechen',
        reply: 'Antworten',
        close: 'Schließen',
    },
    unknownUser: 'Unbekannt',
    createdInDraft: (draftVersion) => `Erstellt in Entwurf ${draftVersion}`,
    editAnnotationPlaceholder: 'Anmerkung schreiben',
    editReplyPlaceholder: 'Antwort schreiben',
    createAnnotationPlaceholder: 'Anmerkung schreiben...',
    createReplyPlaceholder: 'Antwort schreiben...',
    deletedAnnotation: 'Diese Anmerkung wurde gelöscht.',
    edited: 'bearbeitet',
    deleteModalTitle: 'Anmerkung löschen',
    deleteConfirmation: (includeReplies) =>
        includeReplies
            ? 'Sind Sie sicher, dass Sie diese Anmerkung (und alle Antworten) löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.'
            : 'Sind Sie sicher, dass Sie diese Anmerkung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
    notificationTooltip: 'Die Kundin/der Kunde wird über neue Anmerkungen im Tagesbericht informiert.',
    immediatelyVisible: 'Anmerkung sofort sichtbar für Gutachter/in',
}

type TextBubbleProps = {
    type: 'inverted' | 'popup'
    annotation: PdfAnnotation | GeneralAnnotation | DeviationAnnotation
    addReply: (reply: AnnotationMessage, annotationId?: string, onSuccess?: () => void) => void
    deleteAnnotation: (annotationId?: string, cascade?: boolean, onSuccess?: () => void) => void
    deleteReply: (annotationId?: string, replyId?: string, onSuccess?: () => void) => void
    editAnnotation: (updatedText: string, annotationId?: string, onSuccess?: () => void) => void
    editReply: (updatedText: string, annotationId?: string, replyId?: string, onSuccess?: () => void) => void
    isHovered?: boolean
    isCollapsed?: boolean
    onMouseEnter?: () => void
    onMouseLeave?: () => void
    onClick?: () => void
    currentUser?: User
    isAddAnnotationPending?: boolean
    isEditAnnotationPending?: boolean
    isDeleteAnnotationPending?: boolean
    isImmediatelyVisible?: boolean
    showDraftVersion?: boolean
    showNotificationTooltip?: boolean
    draftId?: string
    hasNewerDraft: boolean
    copy?: TextBubbleCopy
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

export function TextBubble({
    type = 'inverted',
    annotation,
    addReply,
    deleteAnnotation,
    deleteReply,
    editAnnotation,
    editReply,
    isHovered,
    isCollapsed,
    onMouseEnter,
    onMouseLeave,
    onClick,
    currentUser,
    isAddAnnotationPending,
    isEditAnnotationPending,
    isDeleteAnnotationPending,
    isImmediatelyVisible,
    showDraftVersion,
    showNotificationTooltip,
    draftId,
    hasNewerDraft,
    copy = DEFAULT_TEXT_BUBBLE_COPY,
    userLinkCopy,
    locale = 'de-DE',
}: TextBubbleProps) {
    const [expanded, setExpanded] = useState(false)
    const [isReplying, setIsReplying] = useState(false)
    const [annotationToEdit, setAnnotationToEdit] = useState<{ annotationId?: string; replyId?: string }>()
    const [annotationToDelete, setAnnotationToDelete] = useState<{ annotationId?: string; replyId?: string; cascade?: boolean }>()
    const options = ({ replyId, isDeleted }: { replyId?: string; isDeleted?: boolean }) => [
        ...(!isDeleted
            ? [
                  {
                      title: copy.actions.edit,
                      icon: <PencilSimpleIcon />,
                      callback: () => {
                          setAnnotationToEdit({ annotationId: annotation.uuid, replyId })
                      },
                  },
              ]
            : []),
        ...(annotation.replies && annotation.replies.length > 0
            ? [
                  ...(!isDeleted
                      ? [
                            {
                                title: copy.actions.deleteAnnotation,
                                icon: <TrashIcon />,
                                callback: () => setAnnotationToDelete({ annotationId: annotation.uuid, replyId }),
                            },
                        ]
                      : []),
                  {
                      title: copy.actions.deleteThread,
                      icon: <TrashIcon />,
                      callback: () => setAnnotationToDelete({ annotationId: annotation.uuid, replyId, cascade: true }),
                  },
              ]
            : [
                  {
                      title: copy.actions.delete,
                      icon: <TrashIcon />,
                      callback: () => setAnnotationToDelete({ annotationId: annotation.uuid }),
                  },
              ]),
    ]

    const handleDelete = () => {
        if (annotationToDelete?.cascade || !annotationToDelete?.replyId) {
            deleteAnnotation(annotationToDelete?.annotationId, annotationToDelete?.cascade, () => setAnnotationToDelete(undefined))
        } else {
            deleteReply(annotationToDelete.annotationId, annotationToDelete.replyId, () => setAnnotationToDelete(undefined))
        }
    }

    const getDraftVersion = (annotation: AnnotationMessage) => {
        const match = annotation.original_draft_title?.match(/(?:Entwurf|Draft)\s*(\d+)/i)
        return match ? match[1] : undefined
    }

    const formatTimestamp = (annotationMessage: AnnotationMessage) => {
        const timestamp = annotationMessage.is_edited ? annotationMessage.updated_on : annotationMessage.created_on
        if (!timestamp) {
            return ''
        }
        return `${getLocalizedDateString(timestamp, false, locale)} ${isoDateStringToLocalizedTime(timestamp, locale)}${
            annotationMessage.is_edited ? ` (${copy.edited})` : ''
        }`
    }

    const [avatarImageSrc, setAvatarImageSrc] = useState(currentUser?.profile_thumbnail_image_url)
    const draftVersion = getDraftVersion(annotation)

    return (
        <TextBubbleContainer
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            $isHovered={isHovered}
            $isCollapsed={isCollapsed}
            $type={type}
            $showBorder={expanded || annotation.is_read_only || isReplying}
        >
            {!isCollapsed ? (
                <>
                    <CommentText $isHovered={isHovered} $type={annotation.is_read_only ? 'inactive' : type}>
                        <Stack direction="row" justifyContent="space-between" gap={tokens.SizingSpacing075} alignItems="center">
                            <Box width="calc(100% - 62px)" overflow="hidden">
                                <DisplayUserLink
                                    user={
                                        !annotation.is_deleted
                                            ? annotation.created_by_user
                                            : {
                                                  first_name: copy.unknownUser,
                                              }
                                    }
                                    size="medium"
                                    inactive={annotation.is_read_only}
                                    defaultIcon={annotation.is_deleted ? <UserCircleDashedIcon /> : undefined}
                                    showProfileOnHover={!annotation.is_deleted}
                                    copy={userLinkCopy}
                                />
                            </Box>

                            <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                {showDraftVersion && draftVersion && (
                                    <Tooltip title={copy.createdInDraft(draftVersion)} placement="top">
                                        <span>
                                            <ContentChip
                                                highContrast
                                                label={draftVersion}
                                                size="small"
                                                color={annotation.original_draft_uuid === draftId ? 'orange' : 'grey'}
                                            />
                                        </span>
                                    </Tooltip>
                                )}

                                {!annotation.is_read_only && (
                                    <IconButtonWithOptions
                                        size="small"
                                        variant="tertiary"
                                        options={options({ replyId: undefined, isDeleted: annotation.is_deleted })}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        {annotationToEdit?.annotationId && !annotationToEdit.replyId ? (
                            <TextBubbleInput
                                onCancel={() => setAnnotationToEdit(undefined)}
                                onSubmit={(text) => editAnnotation(text, annotation.uuid, () => setAnnotationToEdit(undefined))}
                                placeholder={copy.editAnnotationPlaceholder}
                                defaultText={annotation.text}
                                isPending={isEditAnnotationPending}
                                notificationTooltip={copy.notificationTooltip}
                            />
                        ) : !annotation.is_deleted ? (
                            <Typography variant="textS" color={annotation.is_read_only ? tokens.ColorTextDisabled : tokens.ColorTextBold}>
                                {annotation.text}
                            </Typography>
                        ) : (
                            <Typography variant="textS" color={tokens.ColorTextDisabled}>
                                {copy.deletedAnnotation}
                            </Typography>
                        )}

                        <Stack direction="row" height="32px" alignItems="center" justifyContent="space-between">
                            <Typography
                                variant="textXS"
                                color={annotation.is_read_only ? tokens.ColorTextDisabled : tokens.ColorTextDefault}
                            >
                                {annotation.is_deleted ? '-' : formatTimestamp(annotation)}
                            </Typography>
                            {annotation.replies && annotation.replies?.length > 0 ? (
                                <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                    {annotation.is_read_only && annotation.replies.some((reply) => !reply.is_read_only) && (
                                        <Box
                                            sx={{
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: tokens.ColorGraphSequentialSevenMedium,
                                                zIndex: 2,
                                            }}
                                        />
                                    )}
                                    <TinyButton
                                        leftIcon={<ArrowBendDownRightIcon />}
                                        rightIcon={expanded ? <CaretUpIcon /> : <CaretDownIcon />}
                                        label={`${annotation.replies.length}`}
                                        onClick={() => setExpanded((prev) => !prev)}
                                    />
                                </Stack>
                            ) : (
                                <IconButton
                                    size="small"
                                    icon={<ArrowBendDownRightIcon />}
                                    variant="tertiary"
                                    onClick={() => setIsReplying((prev) => !prev)}
                                    tooltip={copy.actions.reply}
                                    disabled={hasNewerDraft}
                                />
                            )}
                        </Stack>
                    </CommentText>

                    {expanded && annotation.replies && annotation.replies.length > 0 && (
                        <Stack direction="column" gap={tokens.SizingSpacing050} padding={tokens.SizingSpacing100} paddingTop={0}>
                            {annotation.replies.map((reply) => {
                                const replyDraftVersion = getDraftVersion(reply)

                                return (
                                    <CommentText
                                        key={reply.uuid}
                                        $type={reply.is_read_only ? 'inactive' : 'inverted'}
                                        $smallBorderRadius
                                        style={{
                                            boxShadow: reply.is_read_only
                                                ? `0 0 0 ${tokens.SizingBorderS} ${tokens.ColorBorderDefault}`
                                                : undefined,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            gap={tokens.SizingSpacing075}
                                            alignItems="center"
                                        >
                                            <Box width="calc(100% - 62px)" overflow="hidden">
                                                <DisplayUserLink
                                                    user={!reply.is_deleted ? reply.created_by_user : { first_name: copy.unknownUser }}
                                                    size="medium"
                                                    defaultIcon={reply.is_deleted ? <UserCircleDashedIcon /> : undefined}
                                                    showProfileOnHover={!reply.is_deleted}
                                                    inactive={reply.is_read_only}
                                                    copy={userLinkCopy}
                                                />
                                            </Box>
                                            <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                                                {showDraftVersion && replyDraftVersion && (
                                                    <Tooltip title={copy.createdInDraft(replyDraftVersion)} placement="top">
                                                        <span>
                                                            <ContentChip
                                                                highContrast
                                                                label={replyDraftVersion}
                                                                size="small"
                                                                color={reply.original_draft_uuid === draftId ? 'orange' : 'grey'}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                )}

                                                {!reply.is_read_only && (
                                                    <IconButtonWithOptions
                                                        size="small"
                                                        variant="tertiary"
                                                        options={options({ replyId: reply.uuid, isDeleted: reply.is_deleted })}
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>
                                        {annotationToEdit?.replyId === reply.uuid ? (
                                            <TextBubbleInput
                                                onCancel={() => setAnnotationToEdit(undefined)}
                                                onSubmit={(text) =>
                                                    editReply(text, annotation.uuid, reply.uuid, () => setAnnotationToEdit(undefined))
                                                }
                                                placeholder={copy.editReplyPlaceholder}
                                                defaultText={reply.text}
                                                isPending={isEditAnnotationPending}
                                                notificationTooltip={copy.notificationTooltip}
                                            />
                                        ) : !reply.is_deleted ? (
                                            <Typography
                                                variant="textS"
                                                color={reply.is_read_only ? tokens.ColorTextDisabled : tokens.ColorTextBold}
                                            >
                                                {reply.text}
                                            </Typography>
                                        ) : (
                                            <Typography variant="textS" color={tokens.ColorTextDisabled}>
                                                {copy.deletedAnnotation}
                                            </Typography>
                                        )}

                                        <Stack direction="row" height="32px" alignItems="center" justifyContent="space-between">
                                            <Typography
                                                variant="textXS"
                                                color={reply.is_read_only ? tokens.ColorTextDisabled : tokens.ColorTextDefault}
                                            >
                                                {reply.is_deleted ? '-' : formatTimestamp(reply)}
                                            </Typography>
                                        </Stack>
                                    </CommentText>
                                )
                            })}

                            {!isReplying && (
                                <Stack direction="row" justifyContent="flex-end">
                                    <TinyButton
                                        leftIcon={<ArrowBendDownRightIcon />}
                                        label={copy.actions.reply}
                                        onClick={() => setIsReplying(!isReplying)}
                                        disabled={hasNewerDraft}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    )}

                    {isReplying && (
                        <Box padding={tokens.SizingSpacing100} paddingTop={0}>
                            <TextBubbleCreation
                                onCancel={() => setIsReplying(false)}
                                onSubmit={(text) => {
                                    addReply({ text }, annotation.uuid, () => {
                                        setIsReplying(false)
                                        setExpanded(true)
                                    })
                                }}
                                placeholder={copy.createReplyPlaceholder}
                                currentUser={currentUser}
                                isPending={isAddAnnotationPending}
                                isImmediatelyVisible={isImmediatelyVisible}
                                showNotificationTooltip={showNotificationTooltip}
                                smallBorderRadius
                                copy={copy}
                                userLinkCopy={userLinkCopy}
                            />
                        </Box>
                    )}
                </>
            ) : (
                <Box width="32px" height="32px" display="flex" alignItems="center" justifyContent="center">
                    <Avatar
                        onError={() => {
                            if (currentUser?.profile_image_url && currentUser.profile_image_url !== avatarImageSrc) {
                                setAvatarImageSrc(currentUser.profile_image_url)
                            }
                        }}
                        src={avatarImageSrc}
                        sx={{ width: tokens.SizingIconS, height: tokens.SizingIconS }}
                    >
                        <UserCircleIcon weight={'bold'} color={tokens.ColorIconLinkDefault} size={tokens.SizingIconS} />
                    </Avatar>
                </Box>
            )}

            <Modal
                footerProps={{
                    primaryButton: {
                        onClick: () => handleDelete(),
                        text: copy.actions.delete,
                        destructive: true,
                        disabled: isDeleteAnnotationPending,
                    },
                    secondaryButton: { onClick: () => setAnnotationToDelete(undefined), text: copy.actions.cancel },
                }}
                isOpen={!!annotationToDelete}
                onClose={() => setAnnotationToDelete(undefined)}
                title={copy.deleteModalTitle}
                showCloseIcon
                closeIconTooltip={copy.actions.close}
            >
                <Typography variant="textM" color={tokens.ColorTextDefault}>
                    {copy.deleteConfirmation(annotationToDelete?.cascade)}
                </Typography>
            </Modal>
        </TextBubbleContainer>
    )
}

type TextBubbleInputProps = {
    onCancel: () => void
    onSubmit: (text: string) => void
    placeholder?: string
    defaultText?: string
    isPending?: boolean
    showNotificationTooltip?: boolean
    notificationTooltip: string
}

function TextBubbleInput({
    onCancel,
    onSubmit,
    placeholder,
    defaultText,
    isPending,
    showNotificationTooltip,
    notificationTooltip,
}: TextBubbleInputProps) {
    const [text, setText] = useState(defaultText ?? '')

    const inputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        const input = inputRef.current
        if (input) {
            input.focus()
            const length = input.value.length
            input.setSelectionRange(length, length)
        }
    }, [])
    return (
        <>
            <InputBase
                value={text}
                inputRef={inputRef}
                onChange={(e) => {
                    setText(e.target.value)
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (text.length > 0) {
                            onSubmit(text)
                        }
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault()
                        onCancel()
                    }
                }}
                multiline
                placeholder={placeholder}
                sx={{
                    width: '100%',
                    padding: tokens.SizingSpacing150,
                    borderRadius: tokens.SizingCornerS,
                    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                    background: tokens.ColorSurfaceDefault,
                    '& .MuiInputBase-input': {
                        ...tokens.TextS,
                        color: tokens.ColorTextBold,
                    },
                }}
                disabled={isPending}
            />
            {isPending ? (
                <div style={{ width: '32px', height: '32px', position: 'relative', alignSelf: 'flex-end' }}>
                    <CircularProgress size={tokens.SizingIconS} sx={{ marginTop: '50%', marginLeft: '50%' }} />
                </div>
            ) : (
                <Stack direction="row" justifyContent="space-between">
                    <IconButton size="small" variant="tertiary" icon={<XIcon />} onClick={onCancel} />
                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                        {showNotificationTooltip && (
                            <Tooltip title={notificationTooltip}>
                                <Stack padding={tokens.SizingSpacing050} alignItems="center">
                                    <ChatCenteredIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconSubtle} />
                                </Stack>
                            </Tooltip>
                        )}
                        <IconButton
                            size="small"
                            variant="primary"
                            icon={<CheckIcon />}
                            disabled={text.length === 0}
                            onClick={() => onSubmit(text)}
                        />
                    </Stack>
                </Stack>
            )}
        </>
    )
}

type TextBubbleCreationProps = {
    onCancel: () => void
    onSubmit: (text: string) => void
    type?: 'inverted' | 'popup'
    placeholder?: string
    currentUser?: User
    isPending?: boolean
    isImmediatelyVisible?: boolean
    showNotificationTooltip?: boolean
    smallBorderRadius?: boolean
    copy?: TextBubbleCopy
    userLinkCopy?: Partial<UserLinkCopy>
}

export function TextBubbleCreation({
    onCancel,
    onSubmit,
    type = 'inverted',
    placeholder,
    currentUser,
    isPending,
    isImmediatelyVisible,
    showNotificationTooltip,
    smallBorderRadius,
    copy = DEFAULT_TEXT_BUBBLE_COPY,
    userLinkCopy,
}: TextBubbleCreationProps) {
    return (
        <Box maxWidth="500px" borderRadius={tokens.SizingCornerM} boxShadow={type === 'popup' ? tokens.Popup.boxShadow : undefined}>
            <CommentText $type={type} $smallBorderRadius={smallBorderRadius}>
                <Stack direction="row" justifyContent={'space-between'} gap={tokens.SizingSpacing075} alignItems="center">
                    <Box width="calc(100% - 58px)" overflow="hidden">
                        <DisplayUserLink user={currentUser} size="medium" copy={userLinkCopy} />
                    </Box>
                </Stack>
                <TextBubbleInput
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                    placeholder={placeholder ?? copy.createAnnotationPlaceholder}
                    isPending={isPending}
                    showNotificationTooltip={showNotificationTooltip}
                    notificationTooltip={copy.notificationTooltip}
                />
                {isImmediatelyVisible && !showNotificationTooltip && (
                    <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center" alignSelf="center">
                        <EyeIcon color={tokens.ColorSurfaceDangerBoldDefault} size={tokens.SizingIconS} weight="bold" />
                        <Typography variant="textS" color={tokens.ColorTextDangerSubtle} noWrap>
                            {copy.immediatelyVisible}
                        </Typography>
                    </Stack>
                )}
            </CommentText>
        </Box>
    )
}

const TextBubbleContainer = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $type: 'inverted' | 'popup'; $isHovered?: boolean; $isCollapsed?: boolean; $showBorder?: boolean }>(
    ({ $type, $isHovered, $isCollapsed, $showBorder }) => ({
        backgroundColor: $isHovered ? '#FDFBE6' : tokens.ColorSurfaceDefault,
        borderRadius: tokens.SizingCornerM,
        gap: tokens.SizingSpacing100,
        display: 'flex',
        flexDirection: 'column',
        width: $isCollapsed ? '32px' : '100%',
        maxWidth: '500px',
        overflow: 'hidden',
        cursor: $isCollapsed ? 'pointer' : 'default',
        boxShadow: [
            `0 0 0 ${$isHovered ? tokens.SizingBorderM : $showBorder ? tokens.SizingBorderS : '0px'} ${$isHovered ? '#F2EF5A' : tokens.ColorBorderDefault}`,
            $type === 'popup' ? tokens.Popup.boxShadow : undefined,
        ]
            .filter(Boolean)
            .join(', '),
    })
)

const CommentText = styled('div', {
    shouldForwardProp: (prop: string) => prop[0] !== '$',
})<{ $type: 'inverted' | 'popup' | 'inactive'; $isHovered?: boolean; $smallBorderRadius?: boolean }>(
    ({ $type, $isHovered, $smallBorderRadius }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.SizingSpacing100,
        backgroundColor: $isHovered ? '#FDFBE6' : $type === 'inverted' ? tokens.ColorSurfaceInverseDefault : tokens.ColorSurfaceDefault,
        borderRadius: $smallBorderRadius ? tokens.SizingCornerS : tokens.SizingCornerM,
        padding: tokens.SizingSpacing100,
    })
)
